/* eslint-disable react/jsx-no-bind */
import React, { memo, useMemo, useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeDashboardDialog } from '@actions/dashboard-actions';
import DialogActions from '@shared/dialogs/dialog-actions';
import {
  changeNotificationFieldValue,
  saveBoundaryNotification
} from '@actions/groups-actions';
import * as dialog from '@constants/dialogs';
import Dialog from '@shared/dialogs/dialog';
import FormControl from '@material-ui/core/FormControl';
import SharedSelectField from '@shared/select-field';
import MenuItem from '@material-ui/core/MenuItem';
import InputSearch from '@shared/input-search';
import { Checkbox } from '@mui';
import { sortByField } from '@utils/data-types-utils';
import ChipInput from 'material-ui-chip-input';
import * as R from 'ramda';
import './create-notifications.scss';


const CreateNotification = () => {
  const dispatch = useDispatch();
  const { activeDialogs, dialogParams } = useSelector(state => state.dashboard);
  const { notificationDetail } = useSelector(state => state.groups);
  const { map_type: entityTypes } = useSelector(state => state.dataTypes);

  const { frequency, recipients, external_recipients, entity_types } = notificationDetail;
  const isDialogActive = activeDialogs[dialog.CREATE_NOTIFICATION];
  const params = {...dialogParams[dialog.CREATE_NOTIFICATION]};

  const [allCheck, setAllCheck] = useState(false);

  const sortedEntityTypes = useMemo(() => sortByField('order')(R.values(entityTypes)), [entityTypes]);
  const frequecies = [
    { id: 0, value: 'Daily' },
    { id: 1, value: 'Weekly on Monday' },
    { id: 2, value: 'Monthly on the 1st' }
  ];

  useEffect(() => {
    dispatch(changeNotificationFieldValue('group', params.group));
    if (params?.data) {
      dispatch(changeNotificationFieldValue('frequency', params.data?.frequency));
      dispatch(changeNotificationFieldValue('recipients', params.data?.recipients));
      dispatch(changeNotificationFieldValue('external_recipients', params.data?.external_recipients));
      dispatch(changeNotificationFieldValue('entity_types', params.data?.entity_types));
    }
  }, [
    params.group,
    params.data,
    dispatch
  ]);

  const handleCancel = () => dispatch(closeDashboardDialog(dialog.CREATE_NOTIFICATION));
  const handleSubmit = () => {
    dispatch(saveBoundaryNotification(notificationDetail, params.data?.id || null));
    dispatch(closeDashboardDialog(dialog.CREATE_NOTIFICATION));
  };

  const onChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      dispatch(changeNotificationFieldValue(name, value));
    }, [dispatch]
  );

  const onRecipientChange = useCallback(
    (name, value) => {
      dispatch(changeNotificationFieldValue(name, value));
    }, [dispatch]
  );

  const handleAddChip = (value) => {
    const parsedVal = value.split(/[\s,]/g).filter(val => val);
    const newList = [...external_recipients];
    dispatch(changeNotificationFieldValue('external_recipients', newList.concat(parsedVal)));
  };

  const handleDeleteChip = (value, index) => {
    const newList = [...external_recipients];
    newList.splice(index, 1);
    dispatch(changeNotificationFieldValue('external_recipients', newList));
  };

  const onAllCheck = () => {
    setAllCheck(!allCheck);
    if (allCheck) {
      dispatch(changeNotificationFieldValue('entity_types', []));
    } else {
      const typeIds = sortedEntityTypes.map(type => type.id);
      dispatch(changeNotificationFieldValue('entity_types', typeIds));
    }
  };

  const onCheck = event => {
    if (entity_types.indexOf(parseInt(event.target.id, 10)) > -1) {
      const newTypes = entity_types.filter(type => type !== parseInt(event.target.id, 10));
      dispatch(changeNotificationFieldValue('entity_types', newTypes));
      setAllCheck(false);
    } else {
      const newTypes = [...entity_types];
      newTypes.push(parseInt(event.target.id, 10));
      dispatch(changeNotificationFieldValue('entity_types', newTypes));
    }
  };

  const submitDisabled = frequency === '' || (recipients.length === 0 && external_recipients.length === 0) || entity_types.length === 0;

  if (isDialogActive) {
    return (
      <Dialog
        actions={
          <DialogActions
            disabledSubmit={submitDisabled}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            submitLabel={params.data?.id ? 'SAVE' : 'CREATE'}
          />
        }
        title="Create notification"
      >
        <div styleName="form-container">
          <FormControl style={{ width: '100%'}}>
            <div styleName="form">
              <div styleName="hint">Send entities in this boundary group as CSV attachments via email to other users.</div>
              <div styleName="row">
                <div>
                  <div styleName="row-title">
                    Frequency
                  </div>
                </div>
                <div>
                  <SharedSelectField
                    fullWidth
                    onChange={onChange}
                    name="frequency"
                    selectStyles={{
                      root: {
                        marginLeft: '0',
                        paddingLeft: '0'
                      }
                    }}
                    value={frequency}
                  >
                    {frequecies.map(freq => (
                      <MenuItem key={freq.id} value={freq.id}>
                        {freq.value}
                      </MenuItem>
                    ))}
                  </SharedSelectField>
                </div>
              </div>
              <div styleName="row">
                <div styleName="row-title">
                  Recipients ({recipients.length})
                </div>
                <div>
                  <InputSearch
                    autocompleteContainerStyles={{ maxWidth: 'none' }}
                    containerStyles={{ margin: '0' }}
                    dataType="user"
                    fieldName={'recipients'}
                    name="recipients"
                    onChange={(event, newValues) => {
                      if (Array.isArray(newValues)) {
                        const values = newValues.map(value => value.value);
                        onRecipientChange('recipients', values);
                      } else {
                        onRecipientChange('recipients', [newValues.value]);
                      }
                    }}
                    label={'Search users'}
                    storePath={['dataTypes', 'user']}
                    values={recipients}
                  />
                </div>
              </div>
              <div styleName="row">
                <div styleName="row-title">
                  External Recipients
                </div>
                <div>
                  <ChipInput
                    fullWidth
                    name="external_recipients"
                    value={external_recipients}
                    onAdd={(chip) => handleAddChip(chip)}
                    onDelete={(chip, index) => handleDeleteChip(chip, index)}
                    newChipKeyCodes={[13, 32]} // create new chip on enter (13) and spacebar (32)
                  />
                </div>
              </div>
              <div styleName="row">
                <div styleName="row-title">
                  Entity types ({entity_types.length})
                </div>
                <div styleName="hint">Select entity types to send as CSV attachments.</div>
                <div styleName="checkbox-list">
                  <Checkbox
                    checked={allCheck}
                    label={'All'}
                    onChange={onAllCheck}
                    name={'all'}
                    size="small"
                  />
                  {sortedEntityTypes.map(entityType => (
                    <Checkbox
                      checked={entity_types.indexOf(entityType.id) > -1}
                      label={entityType.label}
                      onChange={onCheck}
                      id={entityType.id}
                      key={entityType.id}
                      size="small"
                    />
                  ))}
                </div>
              </div>
            </div>
          </FormControl>
        </div>
      </Dialog>
    );
  }
  return null;
};

export default memo(CreateNotification);
